/* eslint-disable react/no-array-index-key */
import {autoFillCode, getNewFormMedicationProfiles} from '@/modules/drug-catalog/functions'
import {useMedicationProfileForm} from '@/modules/drug-catalog/hooks/use-medication-profile-form'
import {useNavigateBackToTradeNameForm} from '@/modules/drug-catalog/hooks/use-navigate-back-to-trade-name-form'
import {mapMedicationProfileFormToApiValues} from '@/modules/drug-catalog/medication-profiles/functions'
import {useGetSolutionDrug} from '@/modules/drug-catalog/solutions/api'
import {SolutionAlternativeCodesTab} from '@/modules/drug-catalog/solutions/components/solution-form/alternative-codes-tab'
import {SolutionEssentialSetupTab} from '@/modules/drug-catalog/solutions/components/solution-form/essential-setup-tab'
import {getSolutionFormDefaultValues} from '@/modules/drug-catalog/solutions/functions'
import {SolutionFormSchema} from '@/modules/drug-catalog/solutions/schemas'
import {useGetExistingCompoundMapping} from '@/modules/smart-compounders/api/use-get-existing-compound-mapping'
import {SmartCompounderSection} from '@/modules/smart-compounders/components/smart-compounder-section'
import {CARRIER_TYPE_VALUES} from '@/modules/smart-compounders/constants'
import {FLAGS} from '@/shared/constants'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, HStack, Tabs} from '@chakra-ui/react'
import {CompoundMappingType} from '@cypro/engine/database/schema'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {LoadingLogo} from '@cypro/ui/components/ui/loading-logo'
import {useBlockerDialog} from '@cypro/ui/hooks/use-blocker-dialog'
import {useGuardNonexistentResourceForm} from '@cypro/ui/hooks/use-guard-nonexistent-resource-form'
import {focusFirstErrorField, setInitialFieldValues, useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {getFirstElementOrSelf, isDefined, removeElementAt} from '@cypro/ui/utils'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {SubmitEvent, useEffect, useState} from 'react'

const TABS = ['essential-setup', 'alternative-codes', 'smart-compounder'] as const
type TabId = (typeof TABS)[number]

interface Props {
  solutionId: string | undefined
}

export function SolutionForm({solutionId}: Props) {
  const {t} = useTranslate('common')
  const {t: tSolutions} = useTranslate('solutions')

  const zero = useZero()
  const navigate = useNavigate()
  const backToTradeName = useNavigateBackToTradeNameForm()

  const [solution, queryState] = useGetSolutionDrug(solutionId)

  const isEditing = Boolean(solutionId)
  const [activeTab, setActiveTab] = useState<TabId>('essential-setup')

  const form = useAppForm({
    defaultValues: getSolutionFormDefaultValues(solution),
    validators: {
      onDynamic: SolutionFormSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit({value}) {
      const payload: Parameters<typeof createSolution>[0] = {
        ...value,
        concentrationUnitId: getFirstElementOrSelf(value.concentrationUnitId),
        medicationProfiles: value.medicationProfiles.map(mapMedicationProfileFormToApiValues),
        machineId: FLAGS.smartCompounder ? value.machineId : undefined,
        carrierType: FLAGS.smartCompounder ? Number(value.carrierType) : undefined,
      }

      if (solutionId) {
        return editSolution({...payload, id: solutionId})
      }

      return createSolution(payload)
    },
    onSubmitInvalid() {
      focusFirstErrorField()
    },
    listeners: {
      onChange({fieldApi, formApi}) {
        if (!isEditing && fieldApi.name === 'name') {
          const isCodePristine = Boolean(formApi.fieldInfo.code?.instance?.state.meta.isPristine)
          const {name, code} = formApi.state.values
          const autoFilled = autoFillCode({name, code, isCodePristine})
          if (!isDefined(autoFilled)) return
          formApi.setFieldValue('code', autoFilled, {dontRunListeners: true, dontUpdateMeta: true})
        }
      },
    },
  })

  const {onAddMedicationProfile, onEditMedicationProfile} = useMedicationProfileForm({
    onSubmit: (value, meta) => {
      form.setFieldValue(
        'medicationProfiles',
        getNewFormMedicationProfiles(form.state.values.medicationProfiles, value, meta.index)
      )
    },
  })

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  useBlockerDialog({shouldBlock: !isDefaultValue})
  useGuardNonexistentResourceForm({resource: solution, queryState, shouldGuard: isEditing})

  const existingMapping = useGetExistingCompoundMapping(CompoundMappingType.enum.CARRIER, solutionId)

  useEffect(() => {
    if (existingMapping) {
      setInitialFieldValues(form, {
        machineId: existingMapping.machineId ?? '',
        carrierType: CARRIER_TYPE_VALUES.parse(String(existingMapping.carrierType ?? 0)),
      })
    }
  }, [existingMapping, form])

  function createSolution(payload: Parameters<typeof zero.mutators.solutions.add>[0]) {
    return zero.attemptMutation(zero.mutators.solutions.add(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('solution_created_successfully')})

      if (await backToTradeName()) return

      form.reset()
      setActiveTab(TABS[0])
    })
  }

  function editSolution(payload: Parameters<typeof zero.mutators.solutions.update>[0]) {
    return zero.attemptMutation(zero.mutators.solutions.update(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('solution_updated_successfully')})

      if (await backToTradeName()) return

      navigate({to: '/configuration/drug-catalog/solutions', ignoreBlocker: true})
    })
  }

  function onSubmit(event: SubmitEvent) {
    event.preventDefault()
    form.handleSubmit()
  }

  function onDeleteMedicationProfile(index: number) {
    form.setFieldValue('medicationProfiles', removeElementAt(form.state.values.medicationProfiles, index))
  }

  if (queryState.type !== 'complete') return <LoadingLogo />

  const submitLabel = !isEditing ? tSolutions('create_solution') : t('save_changes')

  return (
    <form onSubmit={onSubmit}>
      <form.AppForm>
        <Tabs.Root value={activeTab} onValueChange={changes => setActiveTab(changes.value as TabId)}>
          <form.ErrorObserver />

          <Tabs.List>
            <Tabs.Trigger value="essential-setup">
              {tSolutions('essential_setup')}
              <Box as="span" color="fg.error" marginLeft="1">
                *
              </Box>
            </Tabs.Trigger>
            <Tabs.Trigger value="alternative-codes">{tSolutions('alternative_codes')}</Tabs.Trigger>
            {FLAGS.smartCompounder && <Tabs.Trigger value="smart-compounder">{t('smart_compounder')}</Tabs.Trigger>}
          </Tabs.List>

          <Tabs.Content value="essential-setup" paddingTop="6">
            <SolutionEssentialSetupTab
              form={form}
              onAddMedicationProfile={onAddMedicationProfile}
              onEditMedicationProfile={onEditMedicationProfile}
              onDeleteMedicationProfile={onDeleteMedicationProfile}
            />
          </Tabs.Content>

          <Tabs.Content value="alternative-codes" paddingTop="6">
            <SolutionAlternativeCodesTab form={form} />
          </Tabs.Content>

          {FLAGS.smartCompounder && (
            <Tabs.Content value="smart-compounder" paddingTop="6">
              {/* @ts-expect-error */}
              <SmartCompounderSection form={form} mappingType={CompoundMappingType.enum.CARRIER} />
            </Tabs.Content>
          )}

          <HStack paddingY="6" gapX="4" marginTop="6">
            <LinkButton to="/configuration/drug-catalog/solutions" variant="subtle">
              {t('cancel')}
            </LinkButton>

            <form.NextStepButton>{t('next_step')}</form.NextStepButton>

            <Box marginLeft="auto">
              <form.SubmitButton disabled={isEditing && isDefaultValue}>{submitLabel}</form.SubmitButton>
            </Box>
          </HStack>
        </Tabs.Root>
      </form.AppForm>
    </form>
  )
}
